import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Alert,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import platformService from '../../services/platformService';
import usePlatformAuth from '../../hooks/usePlatformAuth';

const COUNTRY_OPTIONS = [
  { code: 'UAE', name: 'United Arab Emirates', currency: 'AED', taxType: 'VAT', defaultTaxRate: '5%', financialYearStart: 'January' },
  { code: 'INDIA', name: 'India', currency: 'INR', taxType: 'GST', defaultTaxRate: '18%', financialYearStart: 'April' },
  { code: 'QATAR', name: 'Qatar', currency: 'QAR', taxType: 'None', defaultTaxRate: '0%', financialYearStart: 'January' },
];

const getStatusColor = (status) => {
  switch (status) {
    case 'active':
      return 'success';
    case 'trial':
      return 'warning';
    case 'suspended':
      return 'error';
    case 'cancelled':
      return 'default';
    default:
      return 'default';
  }
};


export default function TenantList() {
  const navigate = useNavigate();
  const { isDealer, isSuperAdmin } = usePlatformAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Filters
  const [search, setSearch] = useState('');

  // Create Dialog
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm, setCreateForm] = useState({
    companyName: '',
    billingEmail: '',
    country: 'UAE',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: ''
  });

  // Fetch tenants
  const fetchTenants = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await platformService.getTenants({
        page: page + 1,
        limit: rowsPerPage,
        search
      });

      if (response.success) {
        setTenants(response.data.tenants);
        setTotal(response.data.pagination.total);
      }
    } catch (err) {
      setError('Failed to load tenants');
      console.error('Fetch tenants error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTenants();
  }, [page, rowsPerPage, search]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const handleCreateDialogOpen = () => {
    setCreateForm({
      companyName: '',
      billingEmail: '',
      country: 'UAE',
      adminFirstName: 'Admin',
      adminLastName: '',
      adminEmail: ''
    });
    setCreateDialogOpen(true);
  };

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
  };

  // Auto-open create dialog from URL param (e.g., from dashboard button)
  useEffect(() => {
    if (searchParams.get('create') === 'true') {
      handleCreateDialogOpen();
      setSearchParams({});
    }
  }, []);

  const handleCreateFormChange = (field) => (e) => {
    setCreateForm((prev) => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleCreateTenant = async () => {
    // Validation
    if (!createForm.companyName.trim()) {
      toast.error('Company name is required');
      return;
    }
    if (!createForm.adminEmail.trim()) {
      toast.error('Admin email is required');
      return;
    }

    setCreateLoading(true);
    try {
      const response = await platformService.createTenant({
        companyName: createForm.companyName,
        billingEmail: createForm.billingEmail || createForm.adminEmail,
        country: createForm.country,
        adminUser: {
          firstName: createForm.adminFirstName,
          lastName: createForm.adminLastName,
          email: createForm.adminEmail
        }
      });

      if (response.success) {
        toast.success('Tenant created. Login credentials sent to admin email.');
        handleCreateDialogClose();
        fetchTenants();
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to create tenant';
      toast.error(message);
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
          {isDealer() ? 'My Tenants' : 'Tenants'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <IconButton onClick={fetchTenants} disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateDialogOpen} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Create Tenant
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search by company name or email..."
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              )
            }}
          />
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Card>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: { xs: 400, sm: 650 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Company Name</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Email</TableCell>
                <TableCell>Status</TableCell>
                {!isDealer() && <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Dealer</TableCell>}
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Users</TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isDealer() ? 6 : 7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : tenants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isDealer() ? 6 : 7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No tenants found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tenants.map((tenant) => (
                  <TableRow key={tenant.id} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>{tenant.company_name}</Typography>
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>{tenant.billing_email || tenant.details?.email || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={tenant.is_active ? 'Active' : 'Pending'}
                        size="small"
                        color={tenant.is_active ? 'success' : 'warning'}
                      />
                    </TableCell>
                    {!isDealer() && (
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        {tenant.dealer ? tenant.dealer.name : '-'}
                      </TableCell>
                    )}
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {tenant.userCount}
                    </TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                      {new Date(tenant.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => navigate(`/tenants/${tenant.id}`)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Card>

      {/* Create Tenant Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCreateDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Tenant</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Company Information
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Company Name"
                value={createForm.companyName}
                onChange={handleCreateFormChange('companyName')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Billing Email"
                type="email"
                value={createForm.billingEmail}
                onChange={handleCreateFormChange('billingEmail')}
                helperText="Defaults to admin email if empty"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Country / Region</InputLabel>
                <Select
                  value={createForm.country}
                  label="Country / Region"
                  onChange={handleCreateFormChange('country')}
                >
                  {COUNTRY_OPTIONS.map((c) => (
                    <MenuItem key={c.code} value={c.code}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {createForm.country && (() => {
              const selected = COUNTRY_OPTIONS.find(c => c.code === createForm.country);
              return (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                      Auto-configured Settings
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Currency</Typography>
                        <Typography variant="body2">{selected?.currency}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Tax System</Typography>
                        <Typography variant="body2">{selected?.taxType}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Default Tax Rate</Typography>
                        <Typography variant="body2">{selected?.defaultTaxRate}</Typography>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <Typography variant="caption" color="text.secondary">Financial Year Start</Typography>
                        <Typography variant="body2">{selected?.financialYearStart}</Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              );
            })()}

            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 2 }}>
                Initial Admin User
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={createForm.adminFirstName}
                onChange={handleCreateFormChange('adminFirstName')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={createForm.adminLastName}
                onChange={handleCreateFormChange('adminLastName')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Admin Email"
                type="email"
                value={createForm.adminEmail}
                onChange={handleCreateFormChange('adminEmail')}
                required
                helperText="Login credentials will be sent to this email"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateDialogClose}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateTenant}
            disabled={createLoading}
          >
            {createLoading ? <CircularProgress size={20} /> : 'Create Tenant'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
